/**
 * The canvas renderer of focus: presented frames are React Flow nodes moved
 * to their boxes under a 100% camera and every other frame is hidden. Their
 * workspace geometry, the camera and the hidden set wait in the canvas
 * store's `presentationRestore` until the presentation ends.
 */
import type { Node } from "@xyflow/react";
import type {
  FocusAdapter,
  FocusApplyOptions,
  FocusRestoreOptions,
} from "@/lib/focus/focus-adapter";
import { focusBoxesMatch } from "@/lib/focus/focus-plan-layout";
import type {
  FocusMotion,
  PresentationDimsSnapshot,
  PresentationRestore,
  PresentationState,
} from "@/lib/focus/focus-types";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import type { FrameData } from "@/lib/frame-data";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { CANVAS_VIEWPORT_TRANSITION_MS } from "@/lib/canvas-viewport-transitions";
import {
  activePresentationClassName,
  scrubPresentationClasses,
} from "@/lib/presentation-classes";
import {
  restorablePresentationDims,
  restorePresentationDimensions,
  suppressPresentationDimensionPersistence,
  withPresentationDims,
} from "@/lib/presentation-dimension-persistence";
import { isTransientConversationFrameNode } from "@/lib/transient-conversation-frame";
import { useCanvasStore, type CanvasState } from "@/stores/canvas-store";
import {
  SELECTED_FRAME_Z_BASE,
  demotedZIndex,
} from "@/stores/canvas-selection-z";
import { useFramesStore } from "@/stores/frames-store";
import { useSelectionStore } from "@/stores/selection-store";

type FrameNode = Node<FrameData>;

const FOCUS_VIEWPORT = { x: 0, y: 0, zoom: 1 };

/** A node's box in flow coordinates. */
export function focusNodeBox(frameId: string): FocusWindowBox | null {
  const node = useCanvasStore.getState().nodes.find((n) => n.id === frameId);
  return node ? { ...node.position, ...frameNodeSize(node) } : null;
}

function snapshotNodeDims(node: FrameNode): PresentationDimsSnapshot {
  return {
    frameId: node.id,
    position: node.position,
    ...frameNodeSize(node),
    style: node.style,
    measured:
      node.measured?.width != null && node.measured?.height != null
        ? { width: node.measured.width, height: node.measured.height }
        : undefined,
    className: node.className,
  };
}

function sized(node: FrameNode, box: { width: number; height: number }) {
  return {
    width: box.width,
    height: box.height,
    style: { ...node.style, width: box.width, height: box.height },
    measured: { width: box.width, height: box.height },
  };
}

function viewportIsIdentity({ viewport }: CanvasState): boolean {
  return (
    Math.abs(viewport.x) <= 1 &&
    Math.abs(viewport.y) <= 1 &&
    Math.abs(viewport.zoom - 1) <= 0.001
  );
}

/** A reflow is skipped while every focused node already sits in its box. */
function showsAlready(canvas: CanvasState, next: PresentationState): boolean {
  if (next.boxes.length !== next.frameIds.length) return false;
  if (!viewportIsIdentity(canvas)) return false;
  const nodes = new Map(canvas.nodes.map((node) => [node.id, node]));
  return next.frameIds.every((id, index) => {
    const node = nodes.get(id);
    const box = next.boxes[index];
    return Boolean(
      node &&
      box &&
      focusBoxesMatch({ ...node.position, ...frameNodeSize(node) }, box),
    );
  });
}

function restorePoint(
  canvas: CanvasState,
  prev: PresentationState | null,
): PresentationRestore {
  if (prev && canvas.presentationRestore) return canvas.presentationRestore;
  return {
    viewport: { ...canvas.viewport },
    hiddenFrameIds: [...canvas.focusHiddenFrameIds],
    dims: [],
  };
}

/** The frames this apply lays out: focus shows them all, review one. */
function laidOutFrameIds(next: PresentationState): string[] {
  if (next.variant === "focus") return next.frameIds;
  const activeId = next.frameIds[next.activeIndex];
  return activeId ? [activeId] : [];
}

/** Only the window moves when one frame of several is a managed window. */
function movedFrameIds(next: PresentationState): string[] {
  return next.variant === "focus" &&
    next.frameIds.length > 1 &&
    next.focusWindow
    ? [next.focusWindow.frameId]
    : laidOutFrameIds(next);
}

function activeClassName(
  node: FrameNode,
  motion: FocusMotion | null,
  collapsed: boolean,
): string {
  const entering = motion?.frameId === node.id ? motion.kind : null;
  return activePresentationClassName(
    node.className,
    entering === "up" || entering === "down" ? entering : null,
    { collapsed, reveal: entering === "reveal" },
  );
}

function apply(
  prev: PresentationState | null,
  next: PresentationState,
  options: FocusApplyOptions,
): void {
  const canvas = useCanvasStore.getState();
  if (options.reflow && showsAlready(canvas, next)) return;
  const activeId = next.frameIds[next.activeIndex];
  if (!activeId) return;
  const restore = restorePoint(canvas, prev);
  const laidOut = new Set(laidOutFrameIds(next));
  const movedIds = movedFrameIds(next);
  const nodes = new Map(canvas.nodes.map((node) => [node.id, node]));
  const keptDims = restore.dims.filter((dims) => laidOut.has(dims.frameId));
  const kept = new Set(keptDims.map((dims) => dims.frameId));
  const restored = restorablePresentationDims(
    restore.dims.filter((dims) => !laidOut.has(dims.frameId)),
  );
  const newDims = movedIds.flatMap((id) => {
    const node = nodes.get(id);
    if (!node || kept.has(id) || isTransientConversationFrameNode(node)) {
      return [];
    }
    return [
      next.variant === "focus"
        ? snapshotNodeDims(node)
        : { ...snapshotNodeDims(node), position: undefined },
    ];
  });
  movedIds.forEach(suppressPresentationDimensionPersistence);
  const motion =
    next.motion && next.motion !== prev?.motion ? next.motion : null;
  const focusHiddenFrameIds = new Set(
    useFramesStore.getState().frameOrder.filter((id) => !laidOut.has(id)),
  );
  const collapsed = new Set(
    next.customFocusLayout?.kind === "conversation_companion_stack"
      ? next.customFocusLayout.collapsedFrameIds
      : [],
  );
  const box = (id: string) =>
    next.variant === "focus"
      ? next.boxes[next.frameIds.indexOf(id)]
      : next.boxes[0];
  const moved = new Set(movedIds);
  const zIndexFor = (id: string) =>
    next.variant !== "focus" || movedIds.length === 1
      ? SELECTED_FRAME_Z_BASE
      : SELECTED_FRAME_Z_BASE +
        (id === activeId ? movedIds.length : movedIds.indexOf(id));

  useSelectionStore.getState().select(activeId);
  useCanvasStore.setState((current) => ({
    ...(options.entering && {
      isPanMode: false,
      commentModeActive: false,
      notePlacementActive: false,
      commentPopover: null,
    }),
    ...(next.variant === "focus" && { viewport: FOCUS_VIEWPORT }),
    focusHiddenFrameIds,
    presentationRestore: { ...restore, dims: [...keptDims, ...newDims] },
    nodes: current.nodes.map((node) => {
      const snapshot = restored.get(node.id);
      const base = snapshot ? withPresentationDims(node, snapshot) : node;
      const target = moved.has(node.id) ? box(node.id) : undefined;
      if (!target) {
        return {
          ...base,
          hidden:
            !!node.data.metadata?.hidden || focusHiddenFrameIds.has(node.id),
          selected: false,
          zIndex: demotedZIndex(node.zIndex),
          className:
            next.variant === "focus"
              ? scrubPresentationClasses(base.className)
              : base.className,
        };
      }
      return {
        ...base,
        hidden: !!node.data.metadata?.hidden,
        selected: node.id === activeId,
        zIndex: zIndexFor(node.id),
        ...(next.variant === "focus" && {
          position: { x: target.x, y: target.y },
        }),
        ...sized(base, target),
        className: activeClassName(base, motion, collapsed.has(node.id)),
      };
    }),
  }));

  const activeNode = nodes.get(activeId);
  const activeBox = box(activeId);
  const viewport =
    next.variant === "focus"
      ? FOCUS_VIEWPORT
      : activeNode &&
        activeBox && {
          x: activeBox.x - activeNode.position.x,
          y: activeBox.y - activeNode.position.y,
          zoom: 1,
        };
  if (!viewport) return;
  const stepping = motion?.kind === "up" || motion?.kind === "down";
  useCanvasStore.getState().setViewportProgrammatic?.(viewport, {
    duration:
      options.duration ??
      (stepping ? 0 : CANVAS_VIEWPORT_TRANSITION_MS.focusWindow),
  });
}

function restore(_prev: PresentationState, options: FocusRestoreOptions) {
  const point = useCanvasStore.getState().presentationRestore;
  restorePresentationDimensions();
  useCanvasStore.getState().setFocusHiddenFrames(point?.hiddenFrameIds ?? []);
  const viewport = options.restoreViewport ? point?.viewport : undefined;
  useCanvasStore.setState((current) => ({
    ...(viewport && { viewport }),
    presentationRestore:
      options.replaced && point ? { ...point, dims: [] } : null,
    nodes: current.nodes.map((node) => ({
      ...node,
      className: scrubPresentationClasses(node.className),
    })),
  }));
  if (viewport) {
    useCanvasStore
      .getState()
      .setViewportProgrammatic?.(viewport, { duration: 500 });
  }
}

function revealOnWorkspace(frameId: string): void {
  useCanvasStore.getState().selectFrame(frameId);
  requestAnimationFrame(() => {
    useCanvasStore.getState().navigateToFrame(frameId);
  });
}

export const canvasFocusAdapter: FocusAdapter = {
  apply,
  restore,
  revealOnWorkspace,
};
